import React from 'react'
import { TextArea } from '../../../components/form/textInput'
import { Button, ButtonStyle } from '../../../components/simple/button'
import { Tooltip } from '../../../components/simple/tooltip'
import { addDays, addHours, format, isBefore } from 'date-fns'
import { addMinutes, setMinutes, setSeconds, setMilliseconds } from 'date-fns'
import { Divider } from '../../../components/simple/divider'
import { DateTimePicker } from '../../../components/form/timePicker'
import { IconWarningTriangle } from '../../../icons/library'

export const ScheduledTimePicker: React.FC<{
  lexikeet?: boolean
  scheduledTimes?: Date[]
  setScheduledTimes: React.Dispatch<React.SetStateAction<Date[] | undefined>>
  note: string
  setNote: React.Dispatch<React.SetStateAction<string | undefined>>
  singleTime?: boolean
}> = ({ scheduledTimes, setScheduledTimes, note, setNote, lexikeet, singleTime }) => {
  const minDate = React.useMemo(() => roundUpToNearestMinute(addMinutes(new Date(), lexikeet ? 125 : 0)), [])
  const [date, setDate] = React.useState<Date | undefined>(() => {
    const tmr = roundToNearest15Minutes(addDays(new Date(), 1))
    const existing = scheduledTimes?.[0]
    // if single time, the date is linked to scheduledTimes[0] if possible
    return singleTime && existing && !isBefore(existing, minDate) ? existing : tmr
  })

  // if single time, the date is linked to scheduledTimes[0] if possible
  React.useEffect(() => {
    if (singleTime) setScheduledTimes(date ? [date] : [])
  }, [date])

  const [showNote, setShowNote] = React.useState(note.length > 0)

  const alreadyAdded = scheduledTimes?.some((t) => t.getTime() === date?.getTime())

  return (
    <div className={`flex ${singleTime ? 'flex-col gap-2' : 'gap-3'}`}>
      <div className="flex flex-col gap-2">
        <DateTimePicker dateTime={date} setDateTime={setDate} minDate={minDate} />
        {!singleTime && (
          <Button
            disabled={alreadyAdded}
            buttonStyle={ButtonStyle.link}
            small
            onClick={() => {
              if (date) setScheduledTimes((prev) => (prev ? [...prev, date] : [date]))
            }}
          >
            Add Time
          </Button>
        )}
      </div>
      <div className="flex min-w-48 flex-col gap-2">
        {!singleTime && scheduledTimes && scheduledTimes.length > 0 && (
          <div className="flex max-h-64 flex-col gap-1 overflow-auto">
            {(scheduledTimes || []).map((time, i) => (
              <Tooltip text="Click to remove" key={i}>
                <div
                  className="flex cursor-pointer select-none items-center justify-between rounded-sm bg-warning px-2 py-1 text-sm"
                  onClick={() =>
                    setScheduledTimes((prev) => {
                      return prev?.filter((t) => t !== time) || []
                    })
                  }
                >
                  {format(time, 'MMM do h:mmaaa')}
                  {lexikeet && isBefore(time, addHours(new Date(), 12)) && (
                    <IconWarningTriangle
                      className="ml-1"
                      tooltip="Since this time is so soon, an interpreter is not guaranteed to be available."
                    />
                  )}
                </div>
              </Tooltip>
            ))}
          </div>
        )}
        {singleTime && lexikeet && scheduledTimes?.length && isBefore(scheduledTimes[0], addHours(new Date(), 12)) && (
          <div className="max-w-56 rounded bg-warning px-2 py-1 text-sm">
            Since this time is so soon, an interpreter is not guaranteed to be available.
          </div>
        )}
        {showNote ? (
          <TextArea value={note} onChange={setNote} placeholder="Note" className="text-sm" />
        ) : (
          <Divider className="cursor-pointer" onClick={() => setShowNote(true)}>
            Add Note
          </Divider>
        )}
      </div>
    </div>
  )
}

export const ScheduleTimeInstructions: React.FC = () => (
  <div className="m-1 max-w-96">
    <b>To create a conference with a student or guardian:</b>
    <ul className="list-inside list-disc">
      <li>Add one or more times and click “suggest”</li>
      <li>The recipient will receive your message and choose a time that works for them.</li>
      <li>
        If a time is chosen, you will be notified and a conference will be scheduled (with a link and Google Calendar
        invite).{' '}
      </li>
      <li>If no time works for the recipient, they can add a note explaining why and you can try again.</li>
    </ul>
  </div>
)

export const roundToNearest15Minutes = (date: Date) => {
  const minutes = date.getMinutes()
  const roundedMinutes = Math.round(minutes / 15) * 15
  return setMilliseconds(setSeconds(setMinutes(date, roundedMinutes), 0), 0)
}

export const roundUpToNearestMinute = (date: Date): Date => {
  let roundedDate = setSeconds(setMilliseconds(date, 0), 0) // Reset seconds and milliseconds
  if (date.getSeconds() > 0 || date.getMilliseconds() > 0) {
    roundedDate = addMinutes(roundedDate, 1) // Round up to the next minute
  }
  return roundedDate
}
